'use client';

import { cn } from '@/app_lib/utils/utils';

// NOTE: This component was built for desktop first rather than mobile due to the complexity of the logic.
// It is still responsive, but the logic is easier to follow on desktop.

const getLeftRight = (currentPage: number, totalPages: number, middleSlots: number) => {
  const MaxLeftRight = middleSlots - 1;
  const MaxHalf = Math.floor(MaxLeftRight / 2);

  let left = MaxHalf;
  let right = MaxHalf;

  if (totalPages <= middleSlots) {
    // if the total pages are less than the max pages, show all pages
    left = currentPage - 1;
    right = totalPages - currentPage;
  } else if (currentPage >= totalPages - MaxHalf) {
    // if the current page is towards the end, the right should only be the remaining pages
    right = totalPages - currentPage;
    left = MaxLeftRight - right;
  } else if (currentPage <= MaxHalf) {
    // if the current page is towards the beginning, the left should only be the remaining pages
    left = currentPage - 1;
    right = MaxLeftRight - left;
  }

  return [left, right];
};

interface PaginationNumberProps {
  page: number;
  isActive: boolean;
  onClick: () => void;
  className?: string;
}

const PaginationNumber = ({ page, isActive, onClick, className }: PaginationNumberProps) => {
  return (
    <button
      className={cn(
        'text-body-graphik-large text-body-graphik font-semibold font-graphik-condensed',
        'hover:underline cursor-pointer hover:text-accent-primary',
        isActive && 'text-accent-primary cursor-default hover:no-underline',
        className,
      )}
      onClick={isActive ? () => {} : onClick}
      type='button'
    >
      {page}
    </button>
  );
};

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  middleSlots?: number;
  mobileSlots?: number;
  className?: string;
}

export const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  className,
  middleSlots = 5, // values between ellipses, not counting first/last on desktop
  mobileSlots = 3,
}: PaginationProps) => {
  const isActive = (page: number) => currentPage === page;

  if (middleSlots < mobileSlots) {
    middleSlots = mobileSlots;
  }

  middleSlots = middleSlots % 2 === 0 ? middleSlots + 1 : middleSlots; // make sure middle slots are odd

  const [mobileLeft, mobileRight] = getLeftRight(currentPage, totalPages, mobileSlots);

  const MaxHalf = Math.floor(middleSlots / 2); // desktop only logic

  /* Check if the ellipsis and first or last page should be shown */
  // edge case where there are less pages than there are slots
  const totalFirstLast = totalPages > middleSlots;
  const totalEllipses = totalPages > middleSlots + 1;

  const showLeftEllipsis = totalEllipses && currentPage > 1 + (MaxHalf + 1) && middleSlots !== 1;
  const showRightEllipsis = totalEllipses && currentPage < totalPages - (MaxHalf + 1) && middleSlots !== 1;

  const showFirst = totalFirstLast && currentPage >= 1 + (MaxHalf + 1) && middleSlots !== 1;
  const showLast = totalFirstLast && currentPage < totalPages - MaxHalf && middleSlots !== 1;

  /* Find how many pages should exist to the left and right of the current page */
  const [left, right] = getLeftRight(currentPage, totalPages, middleSlots);

  /* Get the range of page values from the left and right of the current page (0-indexed) */
  const leftValues = [...Array.from({ length: left }, (_, i) => currentPage - i - 1)].reverse();
  const rightValues = [...Array.from({ length: right }, (_, i) => currentPage + i + 1)];
  const middlePages = [...leftValues, currentPage, ...rightValues];

  return (
    <div className={cn('@container flex justify-center items-center place-center gap-4 w-full', className)}>
      <button
        className={cn(
          'icon icon-arrow-left border-none',
          currentPage > 1 && 'cursor-pointer hover:text-accent-primary',
          currentPage <= 1 && 'opacity-50',
        )}
        disabled={currentPage <= 1}
        onClick={() => onPageChange(currentPage - 1)}
        type='button'
      >
        <span />
      </button>

      <div className='flex items-center gap-6'>
        {/* always hidden on mobile */}
        <PaginationNumber
          className={cn(showFirst ? 'hidden @2xl:inline' : 'hidden')} // conditionally show first page
          isActive={isActive(1)}
          onClick={() => onPageChange(1)}
          page={1}
        />
        {showLeftEllipsis && <span className='hidden @2xl:inline'>...</span>}
        {/* always hidden on mobile */}

        <ul className='flex items-center gap-6'>
          {middlePages.map((page) => (
            <li
              className={cn(
                // this will hide the difference between mobile and desktop when on mobile
                // So all are rendered, but only the mobilePages are shown on mobile
                page < currentPage - mobileLeft || page > currentPage + mobileRight ? 'hidden @2xl:inline' : '',
              )}
              key={page}
            >
              <PaginationNumber
                isActive={isActive(page)}
                key={page}
                onClick={() => onPageChange(page)}
                page={page}
              />
            </li>
          ))}
        </ul>

        {/* always hidden on mobile */}
        {showRightEllipsis && <span className='hidden @2xl:inline'>...</span>}
        <PaginationNumber
          className={cn(showLast ? 'hidden @2xl:inline' : 'hidden')} // conditionally show last page
          isActive={isActive(totalPages)}
          onClick={() => onPageChange(totalPages)}
          page={totalPages}
        />
        {/* always hidden on mobile */}
      </div>

      <button
        className={cn(
          'icon icon-arrow-right',
          currentPage < totalPages && 'cursor-pointer hover:text-accent-primary',
          currentPage >= totalPages && 'opacity-50',
        )}
        disabled={currentPage >= totalPages}
        onClick={() => onPageChange(currentPage + 1)}
        type='button'
      >
        <span />
      </button>
    </div>
  );
};
